import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-autocomplete-input',
  templateUrl: './autocomplete-input.component.html',
  styleUrls: ['./autocomplete-input.component.css']
})
export class AutocompleteInputComponent implements OnInit, OnDestroy {
  @ViewChild('inputElement', { static: true }) inputElement!: ElementRef<HTMLInputElement>;
  @Input() control!: AbstractControl;
  @Input() placeholder: string = '';
  @Input() suggestions: string[] = [];
  @Input() filterFunction!: (input: string) => Observable<string[]> | string[];
  @Output() valueSelected = new EventEmitter<string>();

  filteredSuggestions: string[] = [];
  showSuggestions: boolean = false;
  private subscription?: Subscription;
  private isInputFocused: boolean = false;

  ngOnInit(): void {
    this.setupAutocomplete();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private setupAutocomplete(): void {
    if (this.control && this.control.valueChanges) {
      this.subscription = this.control.valueChanges.pipe(
        debounceTime(200),
        distinctUntilChanged()
      ).subscribe(value => {
        // Only update suggestions if input is focused
        if (this.isInputFocused) {
          this.updateSuggestions(value || '');
        }
      });
    }
  }

  private updateSuggestions(value: string): void {
    const result = this.filterFunction(value);
    
    if (result instanceof Observable) {
      result.subscribe(filtered => {
        this.filteredSuggestions = filtered;
        this.showSuggestions = this.isInputFocused && filtered.length > 0;
      });
    } else {
      this.filteredSuggestions = result;
      this.showSuggestions = this.isInputFocused && result.length > 0;
    }
  }

  onFocus(): void {
    this.isInputFocused = true;
    const currentValue = this.control.value;
    this.updateSuggestions(currentValue || '');
  }

  onBlur(): void {
    this.isInputFocused = false;
    // Add a small delay to allow clicking on suggestions
    setTimeout(() => {
      if (!this.isInputFocused) {
        this.showSuggestions = false;
      }
    }, 150);
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    this.control.setValue(value);
    
    // Update suggestions immediately on input if focused
    if (this.isInputFocused) {
      this.updateSuggestions(value);
    }
  }

  selectSuggestion(suggestion: string): void {
    this.control.setValue(suggestion);
    this.showSuggestions = false;
    this.isInputFocused = false;
    this.valueSelected.emit(suggestion);
    
    // Optional: keep focus on input after selection
    if (this.inputElement) {
      this.inputElement.nativeElement.focus();
      this.isInputFocused = true;
    }
  }

  onSuggestionMouseDown(event: MouseEvent): void {
    // Prevent blur when clicking on suggestions
    event.preventDefault();
  }

  onContainerClick(event: MouseEvent): void {
    // Ensure focus stays on input when clicking anywhere in the container
    if (this.inputElement && !this.inputElement.nativeElement.contains(event.target as Node)) {
      this.inputElement.nativeElement.focus();
    }
  }
}